import { Flex } from "antd";
import { useCallback } from "react";
import { Option } from "tm-shared/types";

import EmptyGif from "@/assets/images/empty-markets.gif";
import { SORT_OPTIONS } from "@/constants/ui";
import { WEEKDAY_OPTIONS_WITH_ALL } from "@/constants/ui/weekdays";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout";
import { Meta } from "@/shared/seo";
import { BaseButton, PrimaryText, SearchAndSortControls } from "@/shared/ui";
import { setPointsSortOption } from "@/store/slices/ui";

import { AddPointConfirmModal } from "./components/AddPointConfirmModal";
import { PointList } from "./components/PointList";
import { useUnplannedPoints } from "./hooks/useUnplannedPoints";
import { StyledUMEmptyPointGif, StyledUMRadioGroup } from "./Styled";

const UnplannedMarkets = () => {
    const dispatch = useAppDispatch();

    const pointsSortOption = useAppSelector(
        (s) => s.ui.workPlan.pointsSortOption,
    );

    const {
        filteredPoints,
        selectedDay,
        setSelectedDay,
        searchTerm,
        setSearchTerm,
        selectedPoints,
        togglePointSelection,
        openModal,
        setOpenModal,
        pointExtraIds,
        loading,
    } = useUnplannedPoints();

    const hasSelectedItems = selectedPoints.length > 0;

    const handleConfirm = () => {
        if (hasSelectedItems) {
            setOpenModal(true);
        }
    };

    const handleChangeSort = useCallback(
        (option: Option) => {
            dispatch(setPointsSortOption(option));
        },
        [dispatch],
    );

    return (
        <>
            <Meta title="Work Plan" />
            <Header
                title="Додати ТТ"
                back
                needConfirm={hasSelectedItems}
                confirmText="У вас є обрані ТТ які ви ще не додали в поточний маршрут, ви впевнені що хочете вийти?"
            />

            {loading && <Loading />}

            <AddPointConfirmModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                pointExtraIds={pointExtraIds}
                selectedPoints={selectedPoints}
            />

            <MainContainer>
                <Flex vertical gap={8} style={{ marginBottom: 16 }}>
                    <SearchAndSortControls
                        currentSortOption={pointsSortOption}
                        sortOptions={SORT_OPTIONS}
                        onChangeSort={handleChangeSort}
                        query={searchTerm}
                        onChangeQuery={setSearchTerm}
                        searchPlaceholder={"Введіть назву чи адресу"}
                    />

                    <StyledUMRadioGroup
                        options={WEEKDAY_OPTIONS_WITH_ALL}
                        onChange={(e) => setSelectedDay(e.target.value)}
                        value={selectedDay}
                        optionType="button"
                    />
                </Flex>

                <Flex vertical>
                    {filteredPoints.length === 0 ? (
                        <>
                            <div
                                style={{
                                    paddingTop: 16,
                                    marginBottom: 16,
                                }}
                            >
                                <PrimaryText textAlign="center">
                                    За даними парметрами позапланової торгової
                                    точки не знайдено
                                </PrimaryText>
                            </div>

                            <Flex justify="center" align="center">
                                <StyledUMEmptyPointGif
                                    src={EmptyGif}
                                    alt="empty markets"
                                />
                            </Flex>
                        </>
                    ) : (
                        <PointList
                            points={filteredPoints}
                            selectedPoints={selectedPoints}
                            onToggleSelection={togglePointSelection}
                            pointExtraIds={pointExtraIds}
                        />
                    )}
                </Flex>

                <BaseButton
                    type="primary"
                    disabled={selectedPoints.length === 0}
                    onClick={handleConfirm}
                    style={{
                        position: "sticky",
                        bottom: "0",
                        marginTop: "16px",
                    }}
                >
                    Підтвердити ({selectedPoints.length})
                </BaseButton>
            </MainContainer>
        </>
    );
};

export default UnplannedMarkets;
